import { InviteLink } from '@/app/actions/user';
import { LLMMessage, OutlineItem, ReferenceItem } from '@/app/services/llm';
import { SearchModel } from '@/config/site';
import clientRequest from '@/lib/client-request';
import { Key } from 'react';

export interface ConversationItem {
  uid: string;
  sid: string;
  cid: string;
  summary: string;
  ctime: string;
  mtime: string;
  status: string;
  llm_search_request: {
    messages: LLMMessage[];
  };
  query_rewrite: string;
  ref_docs: ReferenceItem[];
  ref_docs_translation: ReferenceItem[];
  summary_translation: string;
  outline_result: OutlineItem | null;
  // v3 元数据（老会话可能为空）
  search_mode?: string;
  summary_style?: string;
  sources?: string[];
}

export interface SessionItem {
  sid: string;
  uid: string;
  ctime: string;
  mtime: string;
  title: string;
  mode: SearchModel;
  conversations: ConversationItem[];
  share_id: string | null;
  share_to_square: boolean;
  style: string;
  user_query: string;
}

export function createSession(
  mode: string,
  user_query: string,
): Promise<DataResponse<SessionItem>> {
  return clientRequest.post('/ai_session/create', {
    user_query,
    mode,
  });
}

export function createSessionV2(
  mode: string,
  user_query: string,
  style?: string,
): Promise<DataResponse<SessionItem>> {
  return clientRequest.post('/v2/ai_session/create', {
    user_query,
    mode,
    style,
  });
}

export interface Author {
  name?: string;
  fore_name?: string;
  last_name?: string;
}

export interface SearchDocsItem {
  doc: ReferenceItem;
  score: null;
  search_gateway: null;
}

export interface AutoSelectDocsItem {
  rewritten_query: string;
}

export function getAutoSelectDocs({
  sid,
  auto_select_option,
  rewritten_query,
  platform,
}: {
  sid: string;
  auto_select_option?: Key;
  rewritten_query?: string;
  platform?: string;
}): Promise<
  DataResponse<{
    rewritten_query: string;
    auto_select_items: ReferenceItem[];
  }>
> {
  return clientRequest.post(
    `/v2/ai_session/${sid}/auto_select_search_items`,
    {
      auto_select_option,
      rewritten_query,
    },
    {
      headers: {
        'X-Platform': platform || 'web',
      },
    },
  );
}

export function getSearchDocs({
  sid,
  page,
  pageSize,
  query,
}: {
  sid: string;
  page: number;
  pageSize: number;
  query: string;
}): Promise<
  DataResponse<{
    page: number;
    page_size: number;
    search_items: SearchDocsItem[];
    auto_select_items: string[];
  }>
> {
  return clientRequest.post(`/v2/ai_session/${sid}/search_docs`, {
    page,
    page_size: pageSize,
    rewritten_query: query,
  });
}

export function getSessionDetail(
  sid: string,
  platform?: string,
): Promise<DataResponse<SessionItem>> {
  return clientRequest.get(`/v2/ai_session/${sid}/detail`, {
    headers: {
      'X-Platform': platform || 'web',
    },
  });
}

export function getSession(sid: string): Promise<DataResponse<SessionItem>> {
  return clientRequest.get(`/ai_session/${sid}/detail`);
}

export function downloadRefs(cid: string, format: string) {
  return clientRequest.get(`/ai_conversation/${cid}/download_docs_citations`, {
    params: {
      format,
    },
    responseType: 'blob',
  });
}

export function downloadRefsV2(pmids: string[]) {
  return clientRequest.post(
    '/v2/doc/download_citations',
    {
      pmids: pmids,
    },
    {
      responseType: 'blob',
    },
  );
}

export interface HistoryItem {
  ctime: string;
  mtime: string;
  sid: string;
  title: string;
  uid: string;
  user_query: string;
  share_to_square: boolean;
  share_id: string | null;
}

export function getSessionHistory({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}): Promise<PageInfo<HistoryItem>> {
  return clientRequest.post(`/ai_session/history`, {
    offset,
    limit,
  });
}

export interface RefArticle {
  proxy_article_url: string;
  article_url: string;
  doi: string;
  title: string;
}

export function getRefArticle(
  cid: string,
  index: number,
): Promise<DataResponse<RefArticle>> {
  return clientRequest.post(
    `/ai_conversation/${cid}/get_article_info/${index}`,
  );
}

export interface ShareLink {
  expire_time: string;
  share_id: string;
  share_pinyin: string;
  share_timestamp: string;
  referral_code: string;
}

export function createShareLink(sid: string): Promise<DataResponse<ShareLink>> {
  return clientRequest.post(`/ai_session/create_share/${sid}`);
}

export function getShareLink(sid: string): Promise<DataResponse<ShareLink>> {
  return clientRequest.get(`/ai_session/get_share/${sid}`);
}

export function getMyRefCode(): Promise<DataResponse<InviteLink>> {
  return clientRequest.get('/user/generate_referral_code');
}

export function generateRewriteQuery(
  sid: string,
  platform?: string,
): Promise<DataResponse<{ rewritten_query: string }>> {
  return clientRequest.post(
    `/v2/ai_session/${sid}/generate_rewritten_query`,
    {},
    {
      headers: {
        'X-Platform': platform || 'web',
      },
    },
  );
}

export function getDocDetail(
  did: string,
): Promise<DataResponse<ReferenceItem>> {
  return clientRequest.get(`v2/doc/${encodeURIComponent(did)}/detail`);
}

export type BatchDocDetail = Partial<ReferenceItem> & {
  publication?: string | null;
  publication_abbr?: string | null;
};

export function getDocDetails(
  docIds: string[],
): Promise<DataResponse<{ docs: Array<BatchDocDetail | null> }>> {
  return clientRequest.post('/v2/doc/batch_detail', {
    doc_ids: docIds,
  });
}
